import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { 
  Briefcase, 
  GraduationCap, 
  Sun, 
  Plane, 
  GripVertical, 
  Save, 
  AlertCircle, 
  CheckCircle,
  Info,
  ArrowUp,
  ArrowDown,
  Clock,
  DollarSign,
  HelpCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VARIANT_ICONS = {
  nebenjob: Briefcase,
  studentenjob: GraduationCap,
  ferienjob: Sun,
  promotour: Plane,
  flexibel: Clock,
  regelmaessig: DollarSign,
  mehr_infos: HelpCircle
};

const VARIANT_COLORS = {
  nebenjob: "text-blue-600 bg-blue-50",
  studentenjob: "text-purple-600 bg-purple-50",
  ferienjob: "text-amber-600 bg-amber-50",
  promotour: "text-emerald-600 bg-emerald-50",
  flexibel: "text-cyan-600 bg-cyan-50",
  regelmaessig: "text-green-600 bg-green-50",
  mehr_infos: "text-slate-600 bg-slate-100"
};

export default function AdminJobVariants({ projectId = "factoryhype" }) {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchVariants();
  }, [projectId]);

  const fetchVariants = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/job-variants/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVariants(response.data.variants || []);
    } catch (err) {
      setError("Fehler beim Laden der Job-Varianten");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Möchtest du die Job-Varianten auf die Standard-Einstellungen zurücksetzen? Alle aktuellen Änderungen gehen verloren.")) {
      return;
    }
    try {
      setResetting(true);
      setError(null);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/job-variants/${projectId}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchVariants();
      setSuccess("Job-Varianten wurden auf Standard zurückgesetzt!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Fehler beim Zurücksetzen: " + (err.response?.data?.detail || err.message));
    } finally {
      setResetting(false);
    }
  };

  const handleToggleActive = (variantId) => {
    setVariants(prev => {
      const updated = prev.map(v => 
        v.id === variantId ? { ...v, active: !v.active } : v
      );
      // Check if at least one is active
      const hasActive = updated.some(v => v.active);
      if (!hasActive) {
        setError("Mindestens eine Job-Variante muss aktiv sein");
        return prev;
      }
      setError(null);
      return updated;
    });
  };

  const handleNameChange = (variantId, newName) => {
    setVariants(prev => 
      prev.map(v => v.id === variantId ? { ...v, name: newName } : v)
    );
  };

  const handleDescriptionChange = (variantId, newDesc) => {
    setVariants(prev => 
      prev.map(v => v.id === variantId ? { ...v, description: newDesc } : v)
    );
  };

  const moveVariant = (index, direction) => {
    setVariants(prev => {
      const newVariants = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newVariants.length) return prev;
      
      // Swap
      [newVariants[index], newVariants[newIndex]] = [newVariants[newIndex], newVariants[index]];
      
      // Update order
      return newVariants.map((v, i) => ({ ...v, order: i + 1 }));
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/job-variants/${projectId}`, 
        { variants },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess("Job-Varianten erfolgreich gespeichert");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = variants.filter(v => v.active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-600" />
              Job-Varianten Einstellungen
            </CardTitle>
            <CardDescription>
              Steuere welche Job-Varianten im Chat angezeigt werden
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={resetting}
            className="text-slate-600 hover:text-red-600 hover:border-red-300"
          >
            {resetting ? "Wird zurückgesetzt..." : "Zurücksetzen"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Box */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>So funktioniert's:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
                <li>Aktiviere/deaktiviere Job-Varianten mit dem Schalter</li>
                <li>Bei <strong>nur 1 aktiven</strong> Variante startet der Chat direkt ohne Auswahl</li>
                <li>Bei <strong>mehreren aktiven</strong> Varianten sieht der Bewerber Buttons zur Auswahl</li>
                <li>Die Reihenfolge bestimmt die Button-Anordnung im Chat</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <span className="font-medium">{activeCount} von {variants.length}</span>
              <span className="text-slate-500 ml-2">Varianten aktiv</span>
            </div>
            <div className="text-sm text-slate-500">
              {activeCount === 1 ? (
                <span className="text-amber-600">Chat startet direkt mit dieser Variante</span>
              ) : (
                <span className="text-emerald-600">Bewerber wählt aus {activeCount} Optionen</span>
              )}
            </div>
          </div>

          {/* Variants List */}
          <div className="space-y-3">
            {variants.map((variant, index) => {
              const Icon = VARIANT_ICONS[variant.id] || Briefcase;
              const colorClass = VARIANT_COLORS[variant.id] || "text-slate-600 bg-slate-50";
              
              return (
                <div 
                  key={variant.id}
                  className={`border rounded-lg p-4 transition-all ${
                    variant.active 
                      ? "border-emerald-200 bg-white shadow-sm" 
                      : "border-slate-200 bg-slate-50 opacity-60"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Drag Handle & Order Buttons */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => moveVariant(index, -1)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-slate-400 font-mono">{index + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => moveVariant(index, 1)}
                        disabled={index === variants.length - 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Icon */}
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <Icon className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor={`name-${variant.id}`} className="text-xs text-slate-500">
                            Button-Text
                          </Label>
                          <Input
                            id={`name-${variant.id}`}
                            value={variant.name}
                            onChange={(e) => handleNameChange(variant.id, e.target.value)}
                            className="max-w-xs"
                            placeholder="Button-Text"
                          />
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Label htmlFor={`active-${variant.id}`} className="text-sm">
                            {variant.active ? "Aktiv" : "Inaktiv"}
                          </Label>
                          <Switch
                            id={`active-${variant.id}`}
                            checked={variant.active}
                            onCheckedChange={() => handleToggleActive(variant.id)}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor={`desc-${variant.id}`} className="text-xs text-slate-500">
                          Kurzbeschreibung (optional)
                        </Label>
                        <Input
                          id={`desc-${variant.id}`}
                          value={variant.description || ""}
                          onChange={(e) => handleDescriptionChange(variant.id, e.target.value)}
                          className="max-w-md"
                          placeholder="z.B. Flexibel neben dem Studium"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">{success}</AlertDescription>
            </Alert>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Änderungen speichern
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vorschau: Chat-Einstieg</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-100 rounded-lg p-4 max-w-md">
            <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
              <p className="text-sm">
                Hey! Welche Art von Job interessiert dich?
              </p>
            </div>
            
            {activeCount === 1 ? (
              <div className="text-center text-sm text-slate-500 italic">
                (Bei nur einer aktiven Variante wird keine Auswahl angezeigt - 
                der Chat startet direkt mit "{variants.find(v => v.active)?.name}")
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {variants
                  .filter(v => v.active)
                  .sort((a, b) => a.order - b.order)
                  .map(v => {
                    const Icon = VARIANT_ICONS[v.id] || Briefcase;
                    return (
                      <button
                        key={v.id}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200 hover:bg-emerald-100"
                      >
                        <Icon className="w-4 h-4" />
                        {v.name}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
